/**
 * usePrinterNetworkConfig
 * -----------------------
 * Reads and saves the printer network configuration from/to the BACKEND database.
 * This means ALL devices on the network (PC, Android, iPhone) automatically share
 * the same printer IP without any per-device setup.
 *
 * Previous approach stored the IP in localStorage, which only worked on the device
 * where it was manually entered — the root cause of mobile printing failures.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

export interface PrinterNetworkConfig {
  ip: string;
  port: number;
  paperWidth: number;
  mode: 'network' | 'windows';
}

const DEFAULTS: PrinterNetworkConfig = {
  ip: '',
  port: 9100,
  paperWidth: 80,
  mode: 'network',
};

// In-memory cache so every component in the same session doesn't refetch
let _cache: PrinterNetworkConfig | null = null;
let _fetchPromise: Promise<PrinterNetworkConfig> | null = null;

export async function fetchPrinterConfig(): Promise<PrinterNetworkConfig> {
  if (_cache) return _cache;
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = api
    .get<PrinterNetworkConfig>('/printer-config')
    .then((res) => {
      _cache = { ...DEFAULTS, ...res.data };
      return _cache;
    })
    .catch(() => {
      // Fallback: read old localStorage keys so existing setups don't break on first deploy
      const ip = localStorage.getItem('printer_ip') || '';
      const port = parseInt(localStorage.getItem('printer_port') || '9100', 10);
      return { ...DEFAULTS, ip, port };
    })
    .finally(() => {
      _fetchPromise = null;
    });

  return _fetchPromise;
}

export function invalidatePrinterConfigCache() {
  _cache = null;
}

export function usePrinterNetworkConfig() {
  const [config, setConfig] = useState<PrinterNetworkConfig>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    fetchPrinterConfig().then((cfg) => {
      if (active) {
        setConfig(cfg);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  const save = useCallback(async (next: PrinterNetworkConfig) => {
    setSaving(true);
    try {
      await api.post('/printer-config', next);
      _cache = next;
      setConfig(next);
    } finally {
      setSaving(false);
    }
  }, []);

  const test = useCallback(async (ip: string, port: number) => {
    const res = await api.post<{ ok: boolean; message: string }>('/printer-config/test', { ip, port });
    return res.data;
  }, []);

  return { config, loading, saving, save, test };
}
